import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  X,
  RefreshCw,
  Mail,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useToast } from '../features/toast/ToastContext';

const SERVICE_LABELS = {
  'air-cargo': 'Air Cargo',
  'sea-cargo': 'Sea Cargo',
  fcl: 'FCL',
  lcl: 'LCL',
  'land-transport': 'Land Transport',
  'customs-clearance': 'Customs Clearance',
  warehousing: 'Warehousing',
  other: 'Other',
};

const STATUS_OPTIONS = ['new', 'in-progress', 'resolved', 'closed'];

function formatDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function statusBadge(status) {
  if (status === 'resolved' || status === 'closed') return 'jet-badge jet-badge-success';
  if (status === 'in-progress') return 'jet-badge jet-badge-warn';
  return 'jet-badge jet-badge-neutral';
}

export default function AdminEnquiriesPanel({ apiBase, authToken, isActive }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [responseText, setResponseText] = useState({});
  const [busyId, setBusyId] = useState(null);

  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    }),
    [authToken],
  );

  const load = useCallback(async () => {
    if (!authToken || !isActive) return;
    setLoading(true);
    setError('');
    try {
      const q = filter !== 'all' ? `?status=${filter}&limit=100` : '?limit=100';
      const res = await fetch(`${apiBase}/api/contact${q}`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to load enquiries');
      setRows(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken, filter, headers, isActive]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      const res = await fetch(`${apiBase}/api/contact/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Update failed');
      setRows((prev) => prev.map((r) => (r._id === id ? json.data : r)));
      toast.success('Enquiry updated');
    } catch (e) {
      toast.error(e.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const sendResponse = async (id) => {
    const message = String(responseText[id] || '').trim();
    if (!message) {
      toast.error('Enter a response message');
      return;
    }
    setBusyId(id);
    try {
      const res = await fetch(`${apiBase}/api/contact/${id}/response`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to save response');
      setRows((prev) => prev.map((r) => (r._id === id ? json.data : r)));
      setResponseText((prev) => ({ ...prev, [id]: '' }));
      toast.success(json.emailSent ? 'Reply emailed to customer' : 'Response saved (configure SMTP to email customers)');
    } catch (e) {
      toast.error(e.message || 'Failed to save response');
    } finally {
      setBusyId(null);
    }
  };

  const newCount = rows.filter((r) => r.status === 'new').length;

  return (
    <div className="jet-card">
      <div className="jet-table-toolbar">
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="jet-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All enquiries</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {newCount > 0 && filter === 'all' && (
            <span className="jet-badge jet-badge-warn">{newCount} new</span>
          )}
        </div>
        <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" onClick={load} disabled={loading} title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error ? <p className="jet-finance-empty" style={{ color: 'var(--jet-danger, #dc2626)' }}>{error}</p> : null}

      {loading && !rows.length ? (
        <p className="jet-finance-empty">Loading enquiries…</p>
      ) : !rows.length ? (
        <p className="jet-finance-empty">No enquiries yet. Website contact form submissions appear here.</p>
      ) : (
        <div className="jet-enquiries-list">
          {rows.map((row) => {
            const open = expandedId === row._id;
            return (
              <article key={row._id} className="jet-enquiry-card">
                <button
                  type="button"
                  className="jet-enquiry-card__head"
                  onClick={() => setExpandedId(open ? null : row._id)}
                >
                  <div>
                    <strong>{row.name}</strong>
                    <span className="jet-enquiry-card__meta">
                      {SERVICE_LABELS[row.service] || row.service} · {formatDate(row.createdAt)}
                    </span>
                  </div>
                  <div className="jet-enquiry-card__head-right">
                    <span className={statusBadge(row.status)}>{row.status}</span>
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </button>

                {open ? (
                  <div className="jet-enquiry-card__body">
                    <div className="jet-enquiry-card__grid">
                      <div><span>Email</span><a href={`mailto:${row.email}`}>{row.email}</a></div>
                      {row.phone ? <div><span>Phone</span>{row.phone}</div> : null}
                      {row.company ? <div><span>Company</span>{row.company}</div> : null}
                    </div>
                    <p className="jet-enquiry-card__message">{row.message}</p>
                    {row.response?.message ? (
                      <div className="jet-enquiry-card__response">
                        <strong>Staff response</strong>
                        <p>{row.response.message}</p>
                        <span>{formatDate(row.response.respondedAt)}</span>
                      </div>
                    ) : null}
                    <div className="jet-enquiry-card__actions">
                      {row.status === 'new' && (
                        <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" disabled={busyId === row._id} onClick={() => updateStatus(row._id, 'in-progress')} title="Mark in progress">
                          <Mail className="w-4 h-4" />
                        </button>
                      )}
                      {row.status !== 'resolved' && row.status !== 'closed' && (
                        <>
                          <textarea
                            className="form-textarea"
                            rows={2}
                            placeholder="Internal response note (saved to record)"
                            value={responseText[row._id] || ''}
                            onChange={(e) => setResponseText((p) => ({ ...p, [row._id]: e.target.value }))}
                          />
                          <button type="button" className="jet-btn jet-btn-secondary" disabled={busyId === row._id} onClick={() => sendResponse(row._id)}>
                            <Check className="w-4 h-4" /> Resolve
                          </button>
                        </>
                      )}
                      {row.status !== 'closed' && (
                        <button type="button" className="jet-btn jet-btn-icon" disabled={busyId === row._id} onClick={() => updateStatus(row._id, 'closed')} title="Close">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
